// SUB-OFFICE 7 — THE ARRIVAL CONTROLLER (increment M-A).
//
// Orchestrates the boot-only walk across the parking lot into the intake hall.
// It is the seam between the FSM's new/absence/reissue decision (game.ts) and
// the exterior scene (exterior.ts): on enter it hides the resident interior,
// dresses the scene for permanent dusk, installs the exterior's own collision
// grid, spawns the petitioner at the far edge in first-person, and lets them
// walk. firstApproach prose fires once at distance gates; the chyron stays
// silent until the threshold. E at the door masks a black frame and hands the
// session back to the existing intake beat inside.
//
// Flows (reasoned through the FSM):
//   NEW (!form1_done)      -> enter('first')  -> cross -> INTAKE_FIRST (TOP lens)
//   REISSUE successor (409)-> enter('first')  -> cross -> INTAKE_FIRST (form1)
//   72h ABSENCE            -> enter('absence')-> cross -> ABSENCE_ACCOUNTING (FP)
//   RETURNING (form1_done) -> never enters; spawns inside as today.

import { Color, DirectionalLight, FogExp2, HemisphereLight, type Scene } from 'three';
import { SPAWN } from '../layout';
import { useZoneCollision, restoreInteriorCollision } from '../collision';
import { buildExterior, type ExteriorBuild } from './exterior';
import type { SceneManager, ViewMode } from '../../render/scene';
import type { FirstPersonView } from '../../render/fpview';
import type { Player } from '../../entities/player';
import type { Fsm } from '../../state';
import type { DomUi } from '../../ui/dom';
import type { EventBus } from '../../entities/entities';

export type ArrivalMode = 'first' | 'absence';

export interface ArrivalDeps {
  scene: Scene;
  sceneMgr: SceneManager;
  player: Player;
  fpv: FirstPersonView;
  fsm: Fsm;
  dom: DomUi;
  bus: EventBus;
  applyView: (v: ViewMode) => void;
  setInteriorVisible: (on: boolean) => void;
  reducedMotion: boolean;
}

interface SavedGrade {
  fogColor: Color;
  fogDensity: number;
  background: Color | null;
  cameraFar: number;
  hemi?: { intensity: number; sky: Color; ground: Color };
  key?: { intensity: number; color: Color };
}

const DUSK_HORIZON = 0x5a4a3a;
const DUSK_FOG = 0x3a3026;

export class ArrivalController {
  private build: ExteriorBuild | null = null;
  private mode: ArrivalMode = 'first';
  private onCross: (() => void) | null = null;
  private crossed = false;
  private active = false;
  private saved: SavedGrade | null = null;
  private firedTriggers: boolean[] = [];
  private chyronTimer = 0;
  private chyronIdx = 0;
  private chyronWoke = false;
  private armed = false;
  private flickerT = 0;
  private keyHandler: ((e: KeyboardEvent) => void) | null = null;

  constructor(private deps: ArrivalDeps) {}

  get isActive(): boolean { return this.active; }

  /** Should this be entered at all? Only new/absence/reissue paths call enter. */
  enter(mode: ArrivalMode, onCross: () => void, absenceLine?: string): void {
    if (this.active) { this.onCross = onCross; this.mode = mode; return; }
    const d = this.deps;
    this.mode = mode;
    this.onCross = onCross;
    this.crossed = false;
    this.chyronWoke = false;
    this.chyronTimer = 0;
    this.chyronIdx = 0;

    // hide the resident interior; build + show the lot
    d.setInteriorVisible(false);
    this.build = buildExterior(d.reducedMotion);
    d.scene.add(this.build.group);
    this.firedTriggers = this.build.approachTriggers.map(() => false);

    // dress the scene for permanent dusk (AFTER-HOURS), remembering the interior grade
    this.dressForDusk();

    // its own collision grid, then spawn at the far edge in first-person
    useZoneCollision({
      walls: this.build.manifest.collision!.walls,
      solids: this.build.manifest.collision!.solids,
      zones: this.build.manifest.collision!.zones,
      bounds: this.build.manifest.bounds,
    });
    const sp = this.build.spawn;
    d.player.setPosition(sp.x, sp.z, sp.facing);
    d.applyView('FP');
    d.fpv.syncFromFacing(sp.facing);

    d.fsm.to('ARRIVAL_EXTERIOR');
    this.active = true;

    // the absence walk is DOWNGRADED: no firstApproach prose, one absence line
    // greeting the EXISTING designation on the cornice (ratified ruling Q4).
    if (mode === 'absence' && absenceLine) {
      this.build.chyron.print(absenceLine);
      this.chyronWoke = true; // do not also cycle the 6 arrival lines
    }

    // the one verb outside: E / Space at the door
    this.keyHandler = (e: KeyboardEvent): void => {
      if ((e.code === 'KeyE' || e.code === 'Space') && !e.repeat && this.armed && this.active) {
        e.preventDefault();
        this.cross();
      }
    };
    window.addEventListener('keydown', this.keyHandler);
  }

  update(dt: number): void {
    if (!this.active || !this.build) return;
    const b = this.build;
    b.chyron.update(dt);

    // the one guttering cobra-head: a hand-keyed loop (the exterior cousin of
    // Window 3). Mostly lit, with sharp irregular dropouts — sodium sickness.
    this.flickerT += dt;
    const p = this.flickerT % 3.4;
    let k = 1;
    if (p < 0.06) k = 0.15; else if (p > 1.5 && p < 1.58) k = 0.25;
    else if (p > 1.62 && p < 1.66) k = 0.55; else if (p > 2.9 && p < 2.96) k = 0.2;
    else k = 0.85 + 0.15 * Math.sin(this.flickerT * 30); // faint 60Hz-ish buzz
    b.setFlicker(k);
    const pz = this.deps.player.pos.z;
    const pdx = this.deps.player.pos.x - b.door.x;
    const pdz = pz - b.door.z;
    const distDoor = Math.hypot(pdx, pdz);

    // firstApproach prose fires once per descending z-gate (first mode only)
    if (this.mode === 'first') {
      for (let i = 0; i < b.approachTriggers.length; i++) {
        if (!this.firedTriggers[i] && pz <= b.approachTriggers[i].z) {
          this.firedTriggers[i] = true;
          this.deps.dom.say(b.approachTriggers[i].line, 6);
        }
      }
      // the chyron wakes only at the threshold, then cycles the arrival lines
      if (pz <= b.thresholdZ) {
        if (!this.chyronWoke) {
          this.chyronWoke = true;
          this.chyronTimer = 0;
          b.chyron.print(b.chyronLines[this.chyronIdx % b.chyronLines.length]);
          this.chyronIdx++;
        } else {
          this.chyronTimer += dt;
          if (this.chyronTimer > 4.6) {
            this.chyronTimer = 0;
            b.chyron.print(b.chyronLines[this.chyronIdx % b.chyronLines.length]);
            this.chyronIdx++;
          }
        }
      }
    }

    // door reach: prompt + arm the interact
    const nowArmed = distDoor <= b.door.reach;
    if (nowArmed !== this.armed) {
      this.armed = nowArmed;
      this.deps.dom.setPrompt(nowArmed ? 'THE DOOR — E TO ENTER — PROCEED' : null);
    }
  }

  /** The threshold: black frame, dispose the lot, hand back to the intake beat. */
  private cross(): void {
    if (this.crossed || !this.active) return;
    this.crossed = true;
    const d = this.deps;
    d.bus.emit('audio:thunk', {});
    d.dom.setPrompt(null);

    const black = document.createElement('div');
    black.style.cssText =
      'position:fixed;inset:0;background:#000;z-index:9999;opacity:1;pointer-events:none';
    document.body.appendChild(black);

    // swap under the black frame on the next frame, then lift it
    requestAnimationFrame(() => {
      this.teardown();
      // 'first' returns to the establishing TOP lens (intake beat is unchanged);
      // 'absence' stays first-person (boot re-asserts FP for ABSENCE_ACCOUNTING).
      if (this.mode === 'first') d.applyView('TOP');
      d.player.setPosition(SPAWN.x, SPAWN.z, SPAWN.facing);
      const cb = this.onCross;
      this.onCross = null;
      if (cb) cb();
      requestAnimationFrame(() => {
        black.style.transition = 'opacity 0.5s ease-out';
        black.style.opacity = '0';
        window.setTimeout(() => black.remove(), 650);
      });
    });
  }

  /** Restore the interior scene state and free the lot. */
  private teardown(): void {
    const d = this.deps;
    if (this.keyHandler) { window.removeEventListener('keydown', this.keyHandler); this.keyHandler = null; }
    this.armed = false;
    this.active = false;
    restoreInteriorCollision();
    this.undressDusk();
    d.setInteriorVisible(true);
    if (this.build) {
      d.scene.remove(this.build.group);
      this.build.dispose();
      this.build = null;
    }
  }

  // --- the dusk grade: swap fog + the two scene lights, remember the interior --
  private dressForDusk(): void {
    const scene = this.deps.scene;
    const fog = scene.fog as FogExp2 | null;
    const cam = this.deps.sceneMgr.camera;
    const saved: SavedGrade = {
      fogColor: fog ? fog.color.clone() : new Color(0x000000),
      fogDensity: fog ? fog.density : 0.012,
      background: scene.background instanceof Color ? scene.background.clone() : null,
      cameraFar: cam.far,
    };
    if (fog) {
      fog.color.setHex(DUSK_FOG).convertSRGBToLinear();
      fog.density = 0.03; // graphics Inc 7: FogExp2 raised so the top is never seen
    }
    if (scene.background instanceof Color) scene.background.setHex(DUSK_HORIZON).convertSRGBToLinear();
    // the monolith rises to y=62 and the sky dome is r200 — both live beyond the
    // interior's 120m far plane, so open the frustum for the lot (fog hides the
    // distance anyway) and restore it on the way in.
    cam.far = 400;
    cam.updateProjectionMatrix();

    for (const o of scene.children) {
      if (o instanceof HemisphereLight) {
        saved.hemi = { intensity: o.intensity, sky: o.color.clone(), ground: o.groundColor.clone() };
        o.intensity = 0.32; // AFTER-HOURS: the lot is nearly dark
        o.color.setHex(0x2c3340).convertSRGBToLinear();      // bruised dusk sky
        o.groundColor.setHex(0x1a1512).convertSRGBToLinear(); // dark asphalt
      } else if (o instanceof DirectionalLight) {
        saved.key = { intensity: o.intensity, color: o.color.clone() };
        o.intensity = 0.12; // a dying warm key, almost gone
        o.color.setHex(0x6a5238).convertSRGBToLinear();
      }
    }
    this.saved = saved;
  }

  private undressDusk(): void {
    const scene = this.deps.scene;
    const s = this.saved;
    if (!s) return;
    const fog = scene.fog as FogExp2 | null;
    if (fog) { fog.color.copy(s.fogColor); fog.density = s.fogDensity; }
    if (s.background && scene.background instanceof Color) scene.background.copy(s.background);
    const cam = this.deps.sceneMgr.camera;
    cam.far = s.cameraFar;
    cam.updateProjectionMatrix();
    for (const o of scene.children) {
      if (o instanceof HemisphereLight && s.hemi) {
        o.intensity = s.hemi.intensity; o.color.copy(s.hemi.sky); o.groundColor.copy(s.hemi.ground);
      } else if (o instanceof DirectionalLight && s.key) {
        o.intensity = s.key.intensity; o.color.copy(s.key.color);
      }
    }
    this.saved = null;
  }
}
